import { Button, Divider, message, Modal, Radio, Row, Segmented, Select, Space, Table, Tabs, Tag, Tooltip, Typography } from "antd";
import type { TableProps } from "antd";
import React, { useEffect, useMemo } from "react";
import Axios from "axios";
import FacturationUsers, { FacturationAccount } from "./facturationUsers";
import { GroupementsFacturation } from "./groupements";
import { DollarOutlined, DownloadOutlined } from "@ant-design/icons";
import { FacturationGroups } from "./facturationGroups";
import { facturationCgvService } from "./facturationCgvService";
import { handleDownloadCSV } from "../cn-softaccess/utils";

const { Link } = Typography;

const formatEur = (n: number) =>
    n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

export interface CnGroupements {
    id: string,
    name: string,
    softaccessType: string,
    prices: {
        "Licence 1 à 10 utilisateurs": number,
        "Licence 11 à 20 utilisateurs": number,
        "Licence 21 à 40 utilisateurs": number,
        "Licence Plus de 40 utilisateurs": number
    } | {
        "Licence principale": number,
        "Licence secondaire": number,
    },
    status?: 'added' | 'modified' | 'deleted'
}

export interface FacturationGroup {
    facturationGroupId: string,
    default: boolean,
    orgaId: string,
    name: string,
    emailFacturation: string,
    agencies: {
        id: string,
        name: string
    }[]
}

export type softaccess = 'cnegoce' | 'multiscan' | 'conquest'
export type SoftaccessToolType = 'cnegoce' | 'mspro'

export interface CollaboratorFacturation {
    coll_id: string
    prename: string
    lastname: string
    email: string
    phone: string
    billed: boolean
    super_admin: boolean
    access_to_facturation_tab?: boolean
}

export interface CGVFacturation {
    cgv: {
        cgv_id: string
        orga_id: string
        cgv_collaborator: string //id of the collaborator
        softaccess_id: string
        softaccess_type: softaccess
        softaccess_name: string
        cgv_validated: boolean
        cgv_validated_by?: string
        cgv_validated_date?: Date
        cgv_version: number
        exists: boolean
        data_json?: {
            raison_sociale: string
            forme_juridique: string
            capital_social: string
            postal_code: string
            city: string
            address: string
            name_rcs: string
            siren: string
            siret: string
            signataire: string
            fonction_signataire: string,
            email_signataire: string
        }
    },
    facturation: {
        id: string
        orga_id: string
        softaccess_id: string
        payment_method: string
        payment_id?: string
        payment_date?: string
        payment_done: boolean
        auto_facturation: boolean
        credit_safe: boolean
        price: number
        tva_enable: boolean
        exists: boolean
        tva_international_number?: string
        register_payment_limit_date?: Date
    }
}


export interface UserPennylaneFacture {
    id: string,
    name: string,
    prename: string,
    email: string,
    rank: number,
    rg2GroupFacturationId?: string
}

export interface Rg2sByOrga {
    orgaId: string,
    rg2s: UserPennylaneFacture[]
}

interface GeneralFacturation {
    cnGroupements: CnGroupements[],
    orgas: InfosByOrga[],
    facturationGroups: FacturationGroup[]
    rg2ByOrga: Rg2sByOrga[]
}

interface InfosByOrga {
    totalPrice: number,
    orga_id: string
    softaccess_id: string
    softaccess_type: SoftaccessToolType
    orga_name: string
    nb_facturation: number
    price: number
    groupement?: string
    facturation_id?: string,
    facturation_name?: string,
    facturation_group_id?: string,
    allSoftAgencies: { id: string, name: string }[],
    /** false / absent = Facturés (si facturation); true = Nos ex's */
    ex_facture?: boolean,
}

type BillingStatusView = 'factures' | 'ex'

function hasFacturation(e: InfosByOrga): boolean {
    return !!(e.facturation_id || e.facturation_group_id)
}

/** Facturés = facturation active ; Nos ex's = ex_facture ou jamais facturé */
function isInExView(e: InfosByOrga): boolean {
    return !!e.ex_facture || !hasFacturation(e)
}

async function getInfosByOrga(setData: React.Dispatch<any>, setLoading: React.Dispatch<any>, allGroupements: React.MutableRefObject<CnGroupements[]>) {
    const result = await facturationCgvService.getInfosByOrga()
    var dataTyped: GeneralFacturation = result.data
    setData(dataTyped)
    allGroupements.current = dataTyped.cnGroupements
    setLoading(false)
}

const TOOL_TYPE_LABEL: Record<SoftaccessToolType, string> = {
    cnegoce: 'S&C',
    mspro: 'MsPro',
}

export default function Facturation() {

    const [data, setData] = React.useState<GeneralFacturation | undefined>()
    const [loading, setLoading] = React.useState<boolean>(true)
    const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
    const [saving, setSaving] = React.useState<boolean>(false)
    const [settingsModalOpened, setSettingsModalOpened] = React.useState<string | undefined>(undefined)
    const [tableView, setTableView] = React.useState<'orga' | 'user'>('orga')
    const [groupementsModalType, setGroupementsModalType] = React.useState<SoftaccessToolType>('cnegoce')
    const [billingStatusView, setBillingStatusView] = React.useState<BillingStatusView>('factures')
    const [accounts, setAccounts] = React.useState<FacturationAccount[]>([])
    const [accountsLoading, setAccountsLoading] = React.useState(true)
    const [orgaFilteredCount, setOrgaFilteredCount] = React.useState<number | null>(null)

    const allGroupements = React.useRef<CnGroupements[]>([]);

    useEffect(() => {
        getInfosByOrga(setData, setLoading, allGroupements)
    }, [])

    useEffect(() => {
        Axios.get('/cnegoces/getAllAccounts')
            .then(res => {
                setAccounts(res.data.map((e: FacturationAccount & { create_date: string }, index: number) => ({
                    ...e,
                    create_date: new Date(e.create_date),
                    notifications: e.notifications || [],
                    key: index,
                })))
            })
            .catch(err => {
                if (Axios.isCancel(err)) return
                console.error(err)
            })
            .finally(() => setAccountsLoading(false))
    }, [])

    const dataUsed = useMemo(
        () => (data?.orgas || []).filter(e =>
            billingStatusView === 'ex' ? isInExView(e) : hasFacturation(e) && !e.ex_facture
        ),
        [data?.orgas, billingStatusView]
    )

    useEffect(() => {
        setOrgaFilteredCount(null)
    }, [billingStatusView, dataUsed, tableView])

    const cnegoceOrgas = useMemo(
        () => (data?.orgas || []).filter(e =>
            e.softaccess_type === 'cnegoce' && hasFacturation(e) && !e.ex_facture
        ),
        [data?.orgas]
    )

    const orgaCounts = useMemo(() => {
        const orgas = data?.orgas || []
        return {
            factures: orgas.filter(o => hasFacturation(o) && !o.ex_facture).length,
            ex: orgas.filter(o => isInExView(o)).length,
        }
    }, [data?.orgas])

    const userCounts = useMemo(() => ({
        factures: accounts.filter(a => !a.ex_facture).length,
        ex: accounts.filter(a => !!a.ex_facture).length,
    }), [accounts])

    const billingCounts = tableView === 'orga' ? orgaCounts : userCounts

    /**
     * Retourne le label du nombre de lignes après filtre (vue orga) format "x / y" ou "y" si pas de filtre
     * @param tab - 'factures' ou 'ex'
     * @returns 
     */
    const tabCountLabel = (tab: BillingStatusView) => {
        const total = billingCounts[tab]
        if (
            tableView === 'orga' &&
            billingStatusView === tab &&
            orgaFilteredCount !== null
        ) {
            return `${orgaFilteredCount} / ${total}`
        }
        return total.toString()
    }

    /**
     * Met à jour le nombre de lignes affichées après filtre (vue orga)
     * @param _pagination - pagination
     * @param filters - filtres
     * @param _sorter - tri
     * @param extra - extra
     */
    const onOrgaTableChange: TableProps<InfosByOrga>['onChange'] = (_pagination, filters, _sorter, extra) => {
        const active = Object.values(filters).some(
            (v) => (Array.isArray(v) ? v.length > 0 : v != null)
        )
        setOrgaFilteredCount(active ? extra.currentDataSource.length : null)
    }

    const priceBreakdown = useMemo(() => {
        const facturedOrgas = (data?.orgas || []).filter(o => hasFacturation(o) && !o.ex_facture)
        return facturedOrgas.reduce(
            (acc, o) => {
                const price = o.totalPrice || 0
                if (o.softaccess_type === 'cnegoce') acc.cnegoce += price
                else if (o.softaccess_type === 'mspro') acc.mspro += price
                acc.total += price
                return acc
            },
            { cnegoce: 0, mspro: 0, total: 0 }
        )
    }, [data?.orgas])

    const toggleIsEx = (record: InfosByOrga, isEx: boolean) => {
        const id = record.facturation_id || record.facturation_group_id || record.softaccess_id
        if (!record.facturation_id && !record.facturation_group_id) {
            message.warning("Aucune facturation à basculer pour cette ligne")
            return
        }
        facturationCgvService.updateIsEx(id, isEx).then(() => {
            message.success(isEx ? "Passé en Nos ex's" : "Remis en Facturés")
            setData({
                ...data!,
                orgas: data!.orgas.map(e => {
                    const sameRow =
                        (record.facturation_group_id && e.facturation_group_id === record.facturation_group_id) ||
                        (record.facturation_id && e.facturation_id === record.facturation_id) ||
                        (!record.facturation_id && !record.facturation_group_id && e.softaccess_id === record.softaccess_id)
                    return sameRow ? { ...e, ex_facture: isEx } : e
                }),
            })
            setSelectedRowKeys([])
        }, () => message.error("Erreur lors de la mise à jour du statut"))
    }

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const groupementsForType = (type: SoftaccessToolType) =>
        (data?.cnGroupements || allGroupements.current)
            .filter(e => e.softaccessType === type)

    const syncGroupementsFromRef = () => {
        if (!data) return
        setData({ ...data, cnGroupements: [...allGroupements.current] })
    }

    const exportOrgaCsv = () => {
        const groupementName = (id?: string) =>
            (data?.cnGroupements || []).find(g => g.id === id)?.name ?? ''

        const escape = (v: string | number) =>
            `"${String(v).replace(/"/g, '""')}"`

        const header = [
            'ID',
            'Nom orga',
            'Groupe facturation',
            "Type d'outil",
            'Groupement',
            'NB Utilisateurs facturés',
            'Prix unitaire',
            'Prix total',
        ]

        const rows = dataUsed.map(o => [
            o.orga_id,
            o.orga_name,
            o.facturation_name ?? '',
            TOOL_TYPE_LABEL[o.softaccess_type],
            groupementName(o.groupement),
            o.nb_facturation,
            o.price,
            o.totalPrice
        ])

        const csv = [header, ...rows]
            .map(r => r.map(escape).join(';'))
            .join('\n')

        handleDownloadCSV(csv, `facturation_orgas_${billingStatusView}.csv`)
    }

    return (
        <>
            <Modal
                open={settingsModalOpened !== undefined}
                onCancel={() => {
                    if (settingsModalOpened === "groupements") syncGroupementsFromRef()
                    setSettingsModalOpened(undefined)
                }}
                footer={null}
                width={800}
                title={settingsModalOpened === "groupements" ? "Gestion des groupements de facturation" : "Gestion des groupes de facturation"}
            >
                {settingsModalOpened == "groupements" && (
                    <Tabs
                        activeKey={groupementsModalType}
                        onChange={(key) => setGroupementsModalType(key as SoftaccessToolType)}
                    >
                        {(['cnegoce', 'mspro'] as SoftaccessToolType[]).map((type) => (
                            <Tabs.TabPane tab={TOOL_TYPE_LABEL[type]} key={type}>
                                <GroupementsFacturation
                                    softaccessType={type}
                                    groupements={groupementsForType(type)}
                                    groupementsRef={allGroupements}
                                />
                            </Tabs.TabPane>
                        ))}
                    </Tabs>
                )}
                {settingsModalOpened == "groups" && (
                    <FacturationGroups
                        rg2s={data?.rg2ByOrga || []}
                        agencies={cnegoceOrgas.map(e => { return { orga_id: e.orga_id, agencies: e.allSoftAgencies } })}
                        groups={data?.facturationGroups || []}
                        organisations={cnegoceOrgas.filter((value, index, self) =>
                            index === self.findIndex((t) => (
                                t.orga_id === value.orga_id
                            ))
                        ).map(e => {
                            return { orga_id: e.orga_id, softaccess_id: e.softaccess_id, name: e.orga_name, defaultGroupId: e.facturation_id || "" }
                        })} saveFacturationGroupsCallback={(selectedOrga, groups, rg2s) => {
                            setData(
                                {
                                    ...data!,
                                    facturationGroups: data!.facturationGroups.filter(e => e.orgaId != selectedOrga).concat(groups),
                                    rg2ByOrga: data!.rg2ByOrga.filter(e => e.orgaId != selectedOrga).concat([{ orgaId: selectedOrga, rg2s: rg2s }])
                                }
                            )
                        }} setSettingsModalOpened={setSettingsModalOpened} />
                )}
            </Modal>
            <Tabs
                activeKey={billingStatusView}
                onChange={(key) => {
                    setBillingStatusView(key as BillingStatusView)
                    setSelectedRowKeys([])
                }}
                items={[
                    { key: 'factures', label: `Nos crushs (${tabCountLabel('factures')})` },
                    { key: 'ex', label: `Nos ex's (${tabCountLabel('ex')})` }
                ]}
                tabBarExtraContent={
                    tableView === 'orga' && billingStatusView === 'factures' && (
                        <Space
                            size={0}
                            split={<Divider type="vertical" style={{ height: 40, margin: '0 16px', borderColor: '#e8e8e8' }} />}
                            style={{
                                background: 'linear-gradient(135deg, #f7f9fc 0%, #f0f5ff 100%)',
                                border: '1px solid #d6e4ff',
                                borderRadius: 10,
                                padding: '10px 20px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                marginBottom: 8
                            }}
                        >
                            <Space style={{ minWidth: 88, textAlign: 'center' }}>
                                <Tag color="blue" style={{ margin: 0 }}>{TOOL_TYPE_LABEL.cnegoce}</Tag>
                                <Typography.Text strong style={{ fontSize: 15, color: '#1677ff' }}>
                                    {formatEur(priceBreakdown.cnegoce)}
                                </Typography.Text>
                            </Space>
                            <Space style={{ minWidth: 88, textAlign: 'center' }}>
                                <Tag color="green" style={{ margin: 0 }}>{TOOL_TYPE_LABEL.mspro}</Tag>
                                <Typography.Text strong style={{ fontSize: 15, color: '#389e0d' }}>
                                    {formatEur(priceBreakdown.mspro)}
                                </Typography.Text>
                            </Space>
                            <Space style={{ minWidth: 110, textAlign: 'center' }}>
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    <DollarOutlined /> Total
                                </Typography.Text>
                                <Typography.Text strong style={{ fontSize: 17, color: '#1f1f1f' }}>
                                    {formatEur(priceBreakdown.total)}
                                </Typography.Text>
                            </Space>
                        </Space>
                    )
                }
            />
            <Space wrap>
                <Radio.Group value={tableView} onChange={e => setTableView(e.target.value)} buttonStyle="solid">
                    <Radio.Button value="orga">Organisation</Radio.Button>
                    <Radio.Button value="user">Utilisateurs</Radio.Button>
                </Radio.Group>
                {tableView === 'orga' ? (
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={exportOrgaCsv}
                        disabled={dataUsed.length === 0}
                    >
                        .csv
                    </Button>
                ) : (
                    <Button
                        type="primary"
                        onClick={() => window.open("/cnegoces/getAllAccountsCSV", '_blank')}
                        icon={<DownloadOutlined />}
                    >
                        .csv
                    </Button>
                )}
            </Space>
            <Space style={{ marginBottom: 16, float: 'right' }} align="start">
                <Button style={{ marginBottom: 16 }} type="primary" onClick={() => setSettingsModalOpened("groupements")}>
                    Gérer les groupements
                </Button>

                {tableView == 'orga' && billingStatusView === 'factures' &&
                    <>
                        <Button style={{ marginBottom: 16 }} type="primary" onClick={() => setSettingsModalOpened("groups")}>
                            Gérer les groupes de facturation
                        </Button>
                        <Button
                            icon={<DollarOutlined />}
                            disabled={saving || selectedRowKeys.length === 0}
                            loading={saving}
                            type="primary"
                            onClick={() => {
                                setSaving(true)
                                facturationCgvService.facturer(
                                    selectedRowKeys.length < (dataUsed?.length || 0) ? selectedRowKeys as (string | number)[] : undefined
                                ).then((res) => {
                                    setSaving(false)
                                    if (res.status == 200) {
                                        message.success("Facturation effectuée avec succès")
                                    } else if (res.status == 207) {
                                        message.warning("Certaines facturations ont échouées")
                                    } else {
                                        message.error("Erreur lors de la facturation")
                                    }
                                }, (err) => {
                                    setSaving(false)
                                    message.error("Erreur lors de la facturation")
                                    console.log(err)
                                })
                            }}
                        >
                            Facturer la sélection ({selectedRowKeys.length})
                        </Button>
                    </>
                }
            </Space>
            {tableView == 'orga' &&
                <Table
                    key={billingStatusView}
                    scroll={{ y: 500 }}
                    loading={loading}
                    pagination={false}
                    onChange={onOrgaTableChange}
                    columns={[
                        {
                            title: 'Orga / Groupe',
                            dataIndex: 'orga_name',
                            key: 'orga_name',
                            filters: [...new Set(dataUsed.map(e => e.orga_name))].map(name => ({ text: name, value: name })),
                            onFilter: (value, record) => value == record.orga_name,
                            filterSearch: true,
                            render: (text: any, record: InfosByOrga) => (
                                <Tooltip title="Cliquez pour accéder à l'organisation" placement="bottom">
                                    <Link href={`/organisations/${record.orga_id}`} style={{ textDecoration: 'underline' }}>
                                        {`${text + " " + (record.facturation_name ? `GroupId: ${record.facturation_name}` : "")}`}
                                    </Link>
                                </Tooltip>
                            )
                        },
                        {
                            title: "Type d'outil",
                            dataIndex: 'softaccess_type',
                            width: 150,
                            key: 'softaccess_type',
                            filters: [
                                { text: TOOL_TYPE_LABEL.cnegoce, value: 'cnegoce' },
                                { text: TOOL_TYPE_LABEL.mspro, value: 'mspro' },
                            ],
                            onFilter: (value, record) => record.softaccess_type === value,
                            render: (type: SoftaccessToolType) => (
                                <Tag color={type === 'mspro' ? 'green' : 'blue'}>{TOOL_TYPE_LABEL[type] || type}</Tag>
                            )
                        },
                        {
                            title: 'Groupement',
                            dataIndex: 'groupement',
                            key: 'groupement',
                            width: 200,
                            filters: [
                                { text: 'Aucun', value: 'undefined' },
                                ...(data?.cnGroupements || []).map(g => ({
                                    text: `${TOOL_TYPE_LABEL[g.softaccessType as SoftaccessToolType] || g.softaccessType} — ${g.name}`,
                                    value: g.id
                                }))
                            ],
                            onFilter: (value: string | number | boolean, record) => {
                                if (value == 'undefined') {
                                    return !record.groupement
                                }
                                return record.groupement == value
                            },
                            filterSearch: true,
                            render: (_text: any, record: InfosByOrga) => {
                                const options = groupementsForType(record.softaccess_type)
                                return <Select style={{ width: 120 }} value={record.groupement} onChange={(value) => {
                                    facturationCgvService.updateGroupement(record.softaccess_id, value).then(() => {
                                        message.success("Groupement mis à jour")
                                        setData({
                                            ...data!,
                                            orgas: data!.orgas.map(e =>
                                                e.softaccess_id === record.softaccess_id
                                                    ? { ...e, groupement: value }
                                                    : e
                                            ),
                                        })
                                    }, () => message.error("Erreur lors de la mise à jour du groupement"))
                                }}>
                                    <Select.Option value={undefined}>Aucun</Select.Option>
                                    {options.map(g => (
                                        <Select.Option key={g.id} value={g.id}>{g.name}</Select.Option>
                                    ))}
                                </Select>
                            }
                        },
                        {
                            title: 'Utilisateurs facturés',
                            dataIndex: 'nb_facturation',
                            width: 200,
                            key: 'nb_facturation',
                            filters: [...new Set(dataUsed.map(e => e.nb_facturation))]
                                .sort((a, b) => a - b)
                                .map(n => ({ text: String(n), value: n })),
                            onFilter: (value, record) => record.nb_facturation === value,
                            filterSearch: true,
                            sorter: (a, b) => a.nb_facturation - b.nb_facturation
                        },
                        {
                            title: 'Prix unitaire',
                            dataIndex: 'price',
                            width: 150,
                            key: 'price',
                            filters: [...new Set(dataUsed.map(e => e.price))]
                                .sort((a, b) => a - b)
                                .map(n => ({ text: formatEur(n), value: n })),
                            onFilter: (value, record) => record.price === value,
                            filterSearch: true,
                            sorter: (a, b) => a.price - b.price,
                            render: (text: any) => formatEur(text)
                        },
                        {
                            title: 'Prix total',
                            dataIndex: 'totalPrice',
                            width: 150,
                            key: 'totalPrice',
                            filters: [...new Set(dataUsed.map(e => e.totalPrice))]
                                .sort((a, b) => a - b)
                                .map(n => ({ text: formatEur(n), value: n })),
                            onFilter: (value, record) => record.totalPrice === value,
                            filterSearch: true,
                            sorter: (a, b) => a.totalPrice - b.totalPrice,
                            render: (text: any) => formatEur(text)
                        },
                        {
                            title: '',
                            key: 'ex_facture',
                            filters: (() => {
                                const hasBilled = dataUsed.some(e => hasFacturation(e))
                                const hasUnbilled = dataUsed.some(e => !hasFacturation(e))
                                if (billingStatusView === 'ex') {
                                    return [
                                        ...(hasBilled ? [{ text: 'Donner une 2nd chance', value: 'ex_billed' }] : []),
                                        ...(hasUnbilled ? [{ text: 'Non facturé', value: 'non_facture' }] : []),
                                    ]
                                }
                                return hasBilled
                                    ? [{ text: 'Briser la relation 💔', value: 'facture' }]
                                    : []
                            })(),
                            onFilter: (value, record) =>
                                value === 'non_facture' ? !hasFacturation(record) : hasFacturation(record),
                            //sorter: (a, b) => Number(hasFacturation(a)) - Number(hasFacturation(b)),
                            render: (_: unknown, record: InfosByOrga) => (
                                record.facturation_id || record.facturation_group_id ? (
                                    <Button
                                        size="small"
                                        danger
                                        onClick={() => toggleIsEx(record, billingStatusView !== 'ex')}
                                        iconPosition="end"
                                    >
                                        {billingStatusView === 'ex' ? 'Donner une 2nd chance' : "Briser la relation 💔"}
                                    </Button>
                                ) : (
                                    <Tag>Non facturé</Tag>
                                )
                            )
                        }
                    ]}
                    rowSelection={billingStatusView === 'factures' ? rowSelection : undefined}
                    dataSource={dataUsed}
                    rowKey={(record) => record.facturation_group_id ?? record.facturation_id ?? `${record.orga_id}-${record.softaccess_id}`}
                />
            }
            {tableView == 'user' &&
                <FacturationUsers
                    billingStatusView={billingStatusView}
                    accounts={accounts}
                    setAccounts={setAccounts}
                    loading={accountsLoading}
                />
            }
        </>
    )
}
